import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../store/useStore';
import { Trophy, LogIn, PlusIcon, Mic, GoalIcon, Search, Siren, Brain, Dumbbell, Flame, Frown, Lock, CalendarHeart, BookOpen, LeafyGreen } from 'lucide-react';
import WordCard from '../components/WordCard';
import { AuthContext } from '../contexts/AuthContext';
import { useWord } from '../contexts/WordContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchLatestCrimeDate, fetchUserAttempts, fetchCurrentUserLeaderboardEntry } from '../services/crimeGameService';
import { checkDailyTrainingCompleted, resetDailyTraining, getUserStats } from '../services/vocabularyService';
import { GameLeaderboardEntry } from '../types/crime-game';
import { crimeStreakMessages, trainingStreakMessages, wordDayStreakMessages, wordsLearnedMessages } from '../utils/streakMessages';

import AudioPlayer from '../components/common/AudioPlayer';
import PWAInstallCard from '../components/PWAInstallCard';
import ChallengeSummaryList from '../components/ChallengeSummaryList';
import { getActiveChallenges, getCompletedChallenges, ChallengeData } from '../services/challengeService';

export default function Dashboard() {
  const { userProgress, savedWords, getWordsPaginated, loadMore, loadingWords, saveUser } = useContext(UserContext);
  const { currentUser } = useContext(AuthContext);
  const [word1, setWord1] = useState('');
  const [word2, setWord2] = useState('');
  const [word3, setWord3] = useState('');
  const [crimeSolved, setCrimeSolved] = useState(false);
  const [trainingCompleted, setTrainingCompleted] = useState(false);
  const [crimeStats, setCrimeStats] = useState<GameLeaderboardEntry | null>(null);
  const [trainingStreak, setTrainingStreak] = useState(0);
  const [wordDayStreak, setWordDayStreak] = useState(0);
  const [crimeMessage, setCrimeMessage] = useState(crimeStreakMessages.inactive[0]);
  const [trainingMessage, setTrainingMessage] = useState(trainingStreakMessages.inactive[0]);
  const [wordDayMessage, setWordDayMessage] = useState(wordDayStreakMessages.inactive[0]);
  const [wordsLearnedMessage, setWordsLearnedMessage] = useState(wordsLearnedMessages.inactive[0]);
  const [activeChallenges, setActiveChallenges] = useState<ChallengeData[]>([]);
  const [completedChallenges, setCompletedChallenges] = useState<ChallengeData[]>([]);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { 
    loadWordOfTheDay,
    getWordOfTheDay,
    currentWord,
    wordLoading,
  } = useWord();

  function getRandomWord() {
    const words = [
      // Fruits
      "apple", "banana", "cherry", "date", "blueberry", "fig", "grape", "melon", 
      "kiwi", "lemon", "mango", "peach", "orange", "papaya", "pear", "raspberry", 
      "strawberry", "tangerine", "plum", "vanilla", "watermelon", "melon", "yam", "squash",
  
      // Animals
      "ant", "bee", "cat", "dog", "elephant", "fox", "goat", "horse", 
      "lizard", "jaguar", "kangaroo", "lion", "monkey", "whale", "owl", "panda", 
      "quail", "rabbit", "shark", "turtle", "starfish", "vulture", "wolf", "squirrel", 
      "yak", "zebra",
  
      // Places or Geographical Features
      "arch", "bridge", "canyon", "desert", "tree", "forest", 
      "geyser", "hill", "island", "jungle", "kitchen", "lake", "mountain", "sky", 
      "oasis", "plain", "mine", "river", "stream", "ice", "valley", "waterfall", 
      "rock", "yard", "peak",
  
      // Professions
      "actor", "builder", "chef", "dancer", "engineer", 
      "farmer", "gardener", "teacher", "doctor", 
      "juggler", "driver", "librarian", "musician", 
      "nurse", "operator", "painter", "queen", "scientist", "teacher", 
      "veterinarian", "writer", "artist", "yogi", "zookeeper",
  
      // Vehicles
      "airplane", "bike", "car", "drone", "elevator", "ferry", "boat", "helicopter", 
      "submarine", "jet", "kayak", 
      "train", "motorcycle", "bus", "truck", 
      "plane", "rocket", "scooter", 
      "tram", "unicycle", "van", "wagon", 
  
      // Musical Instruments
      "accordion", "guitar", "clarinet", "drum", "euphonium", "flute", "guitar", "harp", "instrument", 
      "jug", "keyboard", "lute", "mandolin", "note", "organ", "piano", "note", 
      "recorder", "saxophone", "triangle", "ukulele", "violin", "whistle", "bell", "song", 
      "zither",
  
      // Descriptive Adjectives
      "amazing", "beautiful", "charming", "fun", "elegant", "fascinating", "graceful", 
      "kind", "incredible", "happy", "nice", 
      "magnificent", "bright", "outstanding", 
      "perfect", "quick", "radiant", "stunning", "great", "unique", "vivid", "wonderful"
  ];
    return words[Math.floor(Math.random() * words.length)];
}

  useEffect(() => {
    const w1 = getRandomWord();
    let w2 = getRandomWord();
    while (w2 === w1) {
      w2 = getRandomWord();
    }
    let w3 = getRandomWord();
    while (w3 === w1 || w3 === w2) {
      w3 = getRandomWord();
    }
    setWord1(w1);
    setWord2(w2);
    setWord3(w3);
  }, []);

  useEffect(() => {
    if (currentUser?.uid) {
      setTrainingCompleted(checkDailyTrainingCompleted(currentUser.uid));
      // Fetch training streak
      getUserStats(currentUser.uid).then(stats => {
        setTrainingStreak(stats.streakCount || 0);
        setWordDayStreak(stats.wordDayStreak || 0);
      });
    }
  }, [currentUser, userProgress]); // React to userProgress changes

  useEffect(() => {
    if ((state?.email && state?.name) || currentUser || state?.currentUser) {
      const name = state?.name || currentUser?.displayName || state?.currentUser?.displayName || currentUser?.email.split('@')[0].split('.')[0] || '';
      const email = state?.email || currentUser?.email || state?.currentUser?.email || '';
      saveUser(name, email, currentUser);
    }
  }, [state, currentUser]);

  useEffect(() => {
    loadWordOfTheDay();
    }, []);

  useEffect(() => {
    const checkCrimeStatus = async () => {
      if (!currentUser) return;
      try {
        const dateKey = await fetchLatestCrimeDate();
        if (dateKey) {
          const attempts = await fetchUserAttempts(dateKey, currentUser.uid);
          if (attempts.some(a => a.result === 'win')) {
            setCrimeSolved(true);
          }
        }

        // Fetch streak stats
        const stats = await fetchCurrentUserLeaderboardEntry(currentUser.uid, 'global');
        setCrimeStats(stats);

        // Pick random messages
        const cStreak = stats?.dailyStreak || 0;
        const cMessages = cStreak > 0 ? crimeStreakMessages.active : crimeStreakMessages.inactive;
        setCrimeMessage(cMessages[Math.floor(Math.random() * cMessages.length)]);
      } catch (e) {
        console.error("Failed to check crime status", e);
      }
    };
    checkCrimeStatus();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubActiveChallenges = getActiveChallenges(currentUser.uid, setActiveChallenges);
    const unsubCompletedChallenges = getCompletedChallenges(currentUser.uid, setCompletedChallenges);
    return () => {
      unsubActiveChallenges();
      unsubCompletedChallenges();
    };
  }, [currentUser]);

  useEffect(() => {
    // Set initial training message
    const tStreak = trainingStreak;
    const tMessages = tStreak > 0 ? trainingStreakMessages.active : trainingStreakMessages.inactive;
    setTrainingMessage(tMessages[Math.floor(Math.random() * tMessages.length)]);
  }, [trainingStreak]);

  useEffect(() => {
    // Set initial word day message
    const wStreak = wordDayStreak;
    const wMessages = wStreak > 0 ? wordDayStreakMessages.active : wordDayStreakMessages.inactive;
    setWordDayMessage(wMessages[Math.floor(Math.random() * wMessages.length)]);
  }, [wordDayStreak]);

  useEffect(() => {
    // Set initial words learned message
    const wordsCount = userProgress?.wordsLearned || 0;
    const wlMessages = wordsCount > 0 ? wordsLearnedMessages.active : wordsLearnedMessages.inactive;
    setWordsLearnedMessage(wlMessages[Math.floor(Math.random() * wlMessages.length)]);
  }, [userProgress?.wordsLearned]);

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <div className="p-6 max-w-7xl mx-auto ">
        <PWAInstallCard />

        {currentUser && (<h1 className="text-3xl font-bold text-gray-900 mb-8">Welcome Back!</h1>)}
        
        {!currentUser && (
          <div className='flex flex-col items-center border-2 border-purple-500 rounded-xl p-8 mb-6 bg-gradient-to-br from-purple-50 to-white shadow-lg'>
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <LogIn className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className='text-2xl md:text-3xl font-bold text-purple-700 mb-3'>Make Your Experience Better!</h1>
            <p className='text-lg text-gray-700 text-center mb-4'>Login to access your progress, save words, track your streak, and compete on the leaderboard!</p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 w-full max-w-md">
              <p className="text-sm text-purple-800 font-medium mb-2">
                <strong>What you'll get:</strong>
              </p>
              <ul className="text-left text-sm text-purple-700 space-y-1">
                <li>• Track your learning progress and achievements</li>
                <li>• Save words to your vocabulary</li>
                <li>• Build and maintain your daily streak</li>
                <li>• Compete on the leaderboard</li>
                <li>• Unlock badges and rewards</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
              <button 
                onClick={() => navigate('/login')} 
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all shadow-md hover:shadow-lg"
              >
                <LogIn className='w-5 h-5' />
                Login
              </button>
              <button 
                onClick={() => navigate('/signup')} 
                className="flex-1 px-6 py-3 border-2 border-purple-500 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-all"
              >
                Create Account
              </button>
            </div>
          </div>
        )}
        {currentUser && (<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard
            icon={crimeMessage.icon}
            title="Crime Streak"
            value={`${crimeStats?.dailyStreak || 0} days`}
            color={crimeMessage.color}
            message={crimeMessage.text}
            animateIcon={true}
            onClick={() => navigate('/crime-game')}
          />
          <StatCard
            icon={trainingMessage.icon}
            title="Training Streak"
            value={`${trainingStreak} days`}
            color={trainingMessage.color}
            message={trainingMessage.text}
            animateIcon={true}
            onClick={() => navigate('/training')}
          />
          <StatCard
            icon={wordsLearnedMessage.icon}
            title="Words Learned"
            value={`${userProgress?.wordsLearned || 0}`}
            color={wordsLearnedMessage.color}
            message={wordsLearnedMessage.text}
            animateIcon={true}
            onClick={() => navigate('/vocabulary')}
          />
          <StatCard
            icon={wordDayMessage.icon}
            title="Word of Day Streak"
            value={`${wordDayStreak} days`}
            color={wordDayMessage.color}
            message={wordDayMessage.text}
            animateIcon={true}
            onClick={() => navigate('/day')}
          />
        </div>)}

        {currentUser && (activeChallenges.length > 0 || completedChallenges.length > 0) && (
          <ChallengeSummaryList 
            activeChallenges={activeChallenges} 
            completedChallenges={completedChallenges} 
            currentUser={currentUser} 
          />
        )}

        {wordLoading && !getWordOfTheDay && (
          <div className='flex flex-col items-center border border-purple-200 rounded-xl p-12 bg-gradient-to-br from-purple-50 to-white shadow-lg'>
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-purple-200 rounded-full"></div>
              </div>
              <div className="animate-spin w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full"></div>
            </div>
            <h1 className='text-2xl font-bold text-purple-600 mb-2'>Loading Word of the Day</h1>
            <p className='text-lg text-gray-600 text-center animate-pulse'>
              Preparing today's learning adventure...
            </p>
            <div className="flex justify-center gap-1 mt-4">
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        {getWordOfTheDay && !wordLoading && (<div className='flex flex-col items-center border border-purple-600 rounded p-8 relative'>
            {/* Word of Day Streak Badge */}
            {currentUser && (
               <div className="w-full flex justify-end mb-1 md:absolute md:top-4 md:right-4 md:w-auto md:mb-0">
                 {wordDayStreak > 0 ? (
                   <div className="bg-gradient-to-r from-pink-100 to-rose-100 border-2 border-pink-300 text-pink-700 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md animate-pulse">
                     <CalendarHeart className="w-4 h-4 text-pink-600 fill-pink-600" />
                     <span className="font-bold text-sm">{wordDayStreak} Day Streak</span>
                   </div>
                 ) : (
                   <div className="bg-gray-100 border-2 border-gray-300 text-gray-600 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm animate-pulse">
                     <Frown className="w-4 h-4 text-gray-500 animate-bounce" />
                     <span className="font-bold text-sm">0 Day Streak</span>
                   </div>
                 )}
               </div>
            )}

            <h1 className='text-xl md:text-2xl font-bold text-purple-600 text-center mt-2 md:mt-0'>Expanda o seu vocabulário com uma palavra por dia!</h1>
            <p className='text-lg text-center'>Todos os dias às 7h da manhã, uma nova palavra será apresentada para você com um podcast curto super divertido e com atividades para praticar o conhecimento!</p>
            <p className='text-lg mt-2'>A palavra do dia é:{` `}
              <span className='text-purple-600 font-bold'>{getWordOfTheDay}</span>
            </p>
            {currentWord?.audioUrl && (
              <div className='w-full max-w-md justify-center items-center mx-auto mt-4'>
                <AudioPlayer audioUrl={currentWord.audioUrl} word={currentWord.word.split('')[0].toUpperCase() + currentWord.word.slice(1)} />
              </div>
            )}
            <button onClick={() => navigate('/day')} className="mt-8 px-4 py-2 flex bg-purple-600 text-white rounded hover:bg-purple-700">
              <GoalIcon className='mr-2' />
              Ir para a definição e atividades
            </button>
          </div>)}

        {/* Crime of the Day CTA */}
        {currentUser ? (
          <div
            onClick={() => navigate('/crime-game')}
            className={`w-full flex flex-col items-center border rounded p-6 md:p-8 mt-4 cursor-pointer hover:shadow-lg transition-all relative ${
              crimeSolved
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-indigo-600 bg-indigo-50'
            }`}
          >
             {/* Streak Badge */}
             <div className="w-full flex justify-end mb-2 md:absolute md:top-4 md:right-4 md:w-auto md:mb-0">
               {(crimeStats?.dailyStreak || 0) > 0 ? (
                 <div className="bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 text-orange-700 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md animate-pulse">
                   <Flame className="w-4 h-4 fill-orange-500 text-orange-600" />
                   <span className="font-bold text-sm">{crimeStats?.dailyStreak} Day Streak</span>
                 </div>
               ) : (
                 <div className="bg-gray-100 border-2 border-gray-300 text-gray-600 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm animate-pulse">
                   <Frown className="w-4 h-4 text-gray-500 animate-bounce" />
                   <span className="font-bold text-sm">0 Day Streak</span>
                 </div>
               )}
             </div>

             <h1 className={`text-xl md:text-2xl font-bold flex items-center gap-2 ${
               crimeSolved ? 'text-emerald-700' : 'text-indigo-700'
             }`}>
               {crimeSolved ? <Trophy className="w-8 h-8" /> : <Siren className="w-8 h-8" />}
               Crime of the Day
             </h1>
             <p className='text-lg text-center text-gray-700 mt-2'>
               {crimeSolved
                 ? "Case Closed! You've solved today's mystery. Great work, Detective! Wait for the next case to solve tomorrow!"
                 : "Put your detective hat on! Solve today's mysterious case and climb the leaderboard."
               }
             </p>
             <button
               onClick={(e) => { e.stopPropagation(); navigate('/crime-game'); }}
               className={`mt-6 px-6 py-2 flex items-center text-white rounded-full transition-colors shadow-md ${
                  crimeSolved
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
               }`}
             >
               {crimeSolved ? (
                 <>
                   <Trophy className='mr-2 w-4 h-4' />
                   Mystery Solved
                 </>
               ) : (
                 <>
                   <Search className='mr-2 w-4 h-4' />
                   Solve Mystery
                 </>
               )}
             </button>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center border-2 border-indigo-500 rounded-xl p-4 md:p-8 mt-4 bg-indigo-50 relative">
            <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-purple-100 border border-purple-300 text-purple-700 px-2 py-1 md:px-3 md:py-1 rounded-full flex items-center gap-1 md:gap-1.5 shadow-sm">
              <Lock className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="font-bold text-xs md:text-sm whitespace-nowrap">Login Required</span>
            </div>
            <h1 className='text-xl md:text-2xl font-bold text-indigo-700 flex items-center gap-2 mt-6 md:mt-0'>
              <Siren className="w-8 h-8" />
              Crime of the Day
            </h1>
            <p className='text-lg text-center text-gray-700 mt-2 mb-4'>
              Put your detective hat on! Solve today's mysterious case and climb the leaderboard.
            </p>
            <p className='text-sm text-indigo-600 font-medium mb-4'>
              🔒 Login required to play and track your progress
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
              className="px-6 py-2 flex items-center bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-md"
            >
              <LogIn className='mr-2 w-4 h-4' />
              Login to Play
            </button>
          </div>
        )}

        {/* Daily Training CTA */}
        {currentUser ? (
          trainingCompleted ? (
            <div
              onClick={() => {
                if (currentUser?.uid) {
                  resetDailyTraining(currentUser.uid);
                  navigate('/training');
                }
              }}
              className="w-full flex flex-col items-center border border-yellow-600 rounded p-6 md:p-8 mt-4 bg-yellow-50 cursor-pointer hover:shadow-lg transition-all relative"
            >
               {/* Training Streak Badge - UPDATED (Already present in last edit but ensuring consistency) */}
               <div className="w-full flex justify-end mb-2 md:absolute md:top-4 md:right-4 md:w-auto md:mb-0">
                 {trainingStreak > 0 ? (
                   <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 text-purple-700 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md animate-pulse">
                     <Brain className="w-4 h-4 fill-purple-500 text-purple-600" />
                     <span className="font-bold text-sm">{trainingStreak} Day Streak</span>
                   </div>
                 ) : (
                   <div className="bg-gray-100 border-2 border-gray-300 text-gray-600 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm animate-pulse">
                     <Frown className="w-4 h-4 text-gray-500 animate-bounce" />
                     <span className="font-bold text-sm">0 Day Streak</span>
                   </div>
                 )}
               </div>

               <h1 className='text-xl md:text-2xl font-bold text-yellow-700 flex items-center gap-2'>
                 <Trophy className="w-8 h-8 text-yellow-600" />
                 Daily Training Complete!
               </h1>
               <p className='text-lg text-center text-gray-700 mt-2'>
                 Great job! You've completed your daily exercises. Ready for another round?
               </p>
               
               {/* CTA Cards for Themes and Vocabulary */}
               <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                 <div
                   onClick={(e) => { e.stopPropagation(); navigate('/themes'); }}
                   className="bg-white border-2 border-purple-300 rounded-lg p-4 hover:bg-purple-50 hover:border-purple-400 transition-all cursor-pointer shadow-sm"
                 >
                   <div className="flex items-center gap-3 mb-2">
                     <div className="p-2 bg-purple-100 rounded-lg">
                       <LeafyGreen className="w-5 h-5 text-purple-600" />
                     </div>
                     <h3 className="font-semibold text-gray-800">Practice with Themes</h3>
                   </div>
                   <p className="text-sm text-gray-600 mb-2">
                     Learn vocabulary through themed lessons like Travel, Restaurant, and more.
                   </p>
                   <p className="text-xs text-purple-600 font-medium">
                     ✨ Recommended for beginners
                   </p>
                 </div>
                 
                 <div
                   onClick={(e) => { e.stopPropagation(); navigate('/training');  }}
                   className="bg-white border-2 border-blue-300 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-400 transition-all cursor-pointer shadow-sm"
                 >
                   <div className="flex items-center gap-3 mb-2">
                     <div className="p-2 bg-blue-100 rounded-lg">
                       <BookOpen className="w-5 h-5 text-blue-600" />
                     </div>
                     <h3 className="font-semibold text-gray-800">Learn Vocabulary</h3>
                   </div>
                   <p className="text-sm text-gray-600">
                     Build your vocabulary by adding and practicing new words.
                   </p>
                 </div>
               </div>
               
               <button
                 onClick={(e) => {
                   e.stopPropagation();
                   if (currentUser?.uid) {
                      resetDailyTraining(currentUser.uid);
                      navigate('/training');
                   }
                 }}
                 className="mt-6 px-6 py-2 flex items-center bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors shadow-md"
               >
                 <Dumbbell className='mr-2 w-4 h-4' />
                 Start New Round
               </button>
            </div>
          ) : (
            <div
              onClick={() => navigate('/training')}
              className="w-full flex flex-col items-center border border-yellow-600 rounded p-6 md:p-8 mt-4 bg-yellow-50 cursor-pointer hover:shadow-lg transition-all relative"
            >
               {/* Training Streak Badge */}
               <div className="w-full flex justify-end mb-2 md:absolute md:top-4 md:right-4 md:w-auto md:mb-0">
                 {trainingStreak > 0 ? (
                   <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 text-purple-700 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md animate-pulse">
                     <Brain className="w-4 h-4 fill-purple-500 text-purple-600" />
                     <span className="font-bold text-sm">{trainingStreak} Day Streak</span>
                   </div>
                 ) : (
                   <div className="bg-gray-100 border-2 border-gray-300 text-gray-600 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm animate-pulse">
                     <Frown className="w-4 h-4 text-gray-500 animate-bounce" />
                     <span className="font-bold text-sm">0 Day Streak</span>
                   </div>
                 )}
               </div>

               <h1 className='text-xl md:text-2xl font-bold text-yellow-700 flex items-center gap-2'>
                 <Brain className="w-8 h-8" />
                 Daily Training
               </h1>
               <p className='text-lg text-center text-gray-700 mt-2'>
                 Boost your memory! Complete your daily vocabulary exercises to master new words.
               </p>
               
               {/* CTA Cards for Themes and Vocabulary */}
               <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                 <div
                   onClick={(e) => { e.stopPropagation(); navigate('/themes'); }}
                   className="bg-white border-2 border-purple-300 rounded-lg p-4 hover:bg-purple-50 hover:border-purple-400 transition-all cursor-pointer shadow-sm"
                 >
                   <div className="flex items-center gap-3 mb-2">
                     <div className="p-2 bg-purple-100 rounded-lg">
                       <LeafyGreen className="w-5 h-5 text-purple-600" />
                     </div>
                     <h3 className="font-semibold text-gray-800">Practice with Themes</h3>
                   </div>
                   <p className="text-sm text-gray-600 mb-2">
                     Learn vocabulary through themed lessons like Travel, Restaurant, and more.
                   </p>
                   <p className="text-xs text-purple-600 font-medium">
                     ✨ Recommended for beginners
                   </p>
                 </div>
                 
                 <div
                   onClick={(e) => { e.stopPropagation(); navigate('/training'); }}
                   className="bg-white border-2 border-blue-300 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-400 transition-all cursor-pointer shadow-sm"
                 >
                   <div className="flex items-center gap-3 mb-2">
                     <div className="p-2 bg-blue-100 rounded-lg">
                       <BookOpen className="w-5 h-5 text-blue-600" />
                     </div>
                     <h3 className="font-semibold text-gray-800">Learn Vocabulary</h3>
                   </div>
                   <p className="text-sm text-gray-600">
                     Build your vocabulary by adding and practicing new words.
                   </p>
                 </div>
               </div>
               
               <button
                 onClick={(e) => { e.stopPropagation(); navigate('/training'); }}
                 className="mt-6 px-6 py-2 flex items-center bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition-colors shadow-md"
               >
                 <Dumbbell className='mr-2 w-4 h-4' />
                 Start Training
               </button>
            </div>
          )
        ) : (
          <div className="w-full flex flex-col items-center border-2 border-yellow-500 rounded-xl p-4 md:p-8 mt-4 bg-yellow-50 relative">
            <div className="absolute top-2 right-2 md:top-4 md:right-4 bg-purple-100 border border-purple-300 text-purple-700 px-2 py-1 md:px-3 md:py-1 rounded-full flex items-center gap-1 md:gap-1.5 shadow-sm">
              <Lock className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              <span className="font-bold text-xs md:text-sm whitespace-nowrap">Login Required</span>
            </div>
            <h1 className='text-xl md:text-2xl font-bold text-yellow-700 flex items-center gap-2 mt-6 md:mt-0'>
              <Brain className="w-8 h-8" />
              Daily Training
            </h1>
            <p className='text-lg text-center text-gray-700 mt-2 mb-4'>
              Boost your memory! Complete your daily vocabulary exercises to master new words.
            </p>
            <p className='text-sm text-yellow-600 font-medium mb-4'>
              🔒 Login required to track your progress and achievements
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
              className="px-6 py-2 flex items-center bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition-colors shadow-md"
            >
              <LogIn className='mr-2 w-4 h-4' />
              Login to Start Training
            </button>
          </div>
        )}

        <div className='flex flex-col md:flex-row gap-4 items-stretch'>

          <div onClick={() => false && navigate('/themes')} data-testid="themes" className='w-full justify-center md:w-1/3 flex flex-col cursor-pointer items-center border border-purple-600 rounded p-4 mt-4 hover:bg-purple-100 hover:shadow-lg'>
            <h1 className='text-center text-xl md:text-2xl font-bold text-purple-600'>Learn with themes!</h1>
            <p className='text-center text-lg mt-2'>Choose the theme you want to study like traveling, food and more to learn new words!</p>
            <div className='w-full flex gap-4 justify-center'>
              <div onClick={() => navigate(`/theme`, { state: { themeName: 'Travel', index : 0 } })} data-testid='themes-name' className='text-center text-lg mt-2 border bg-white px-5 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <p>✈️ Travel</p>
              </div>
              <div onClick={() => navigate(`/theme`, { state: { themeName: 'Restaurant', index : 1 } })} data-testid='themes-name' className='text-center text-lg mt-2 border bg-white px-5 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <p>🍽️ Restaurant</p>
              </div>
            </div>
            <div className='w-full flex gap-4 justify-center'>
              <div onClick={() => navigate(`/theme`, { state: { themeName: 'Hotel', index : 2 } })} data-testid='themes-name' className='text-center text-lg mt-2 border bg-white px-5 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <p>🏨 Hotel</p>
              </div>
              <div onClick={() => navigate('/themes')} data-testid='themes-name' className='text-center flex justify-center items-center text-lg mt-2 border bg-white px-5 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <PlusIcon />
                <p> More</p>
              </div>
            </div>
          </div>

          <div onClick={() => false && navigate('/speaking')} className='w-full justify-center md:w-1/3 flex flex-col cursor-pointer items-center border border-purple-600 rounded p-4 mt-4 hover:bg-purple-100 hover:shadow-lg'>
            <h1 className='text-center text-xl md:text-2xl font-bold text-purple-600'>Let's practice the pronunciation!</h1>
            <p className='text-center text-lg mt-2'>Practice how to speak the words you want to learn</p>
            <div className='w-full flex gap-4 justify-center'>
              <div data-testid='speaking-word' onClick={() => navigate('/speaking', { state: { word: word1 } })} className='flex text-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
              <Mic className='w-5 h-5 mr-2 text-purple-500' />
                <p>{word1}</p>
              </div>
              <div data-testid='speaking-word' onClick={() => navigate('/speaking', { state: { word: word2 } })} className='flex text-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <Mic className='w-5 h-5 mr-2 text-purple-500' />
                <p>{word2}</p>
              </div>
            </div>
            <div className='w-full flex gap-4 justify-center'>
              <div data-testid='speaking-word' onClick={() => navigate('/speaking', { state: { word: word3 } })} className='flex text-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <Mic className='w-5 h-5 mr-2 text-purple-500' />
                <p>{word3}</p>
              </div>
              <div data-testid='speaking-more' onClick={() => navigate('/speaking')} className='text-center flex justify-center items-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <PlusIcon />
                <p> More</p>
              </div>
            </div>
          </div>

          <div onClick={() => false && navigate('/expressions')} className='w-full justify-center md:w-1/3 flex flex-col cursor-pointer items-center border border-purple-600 rounded p-4 mt-4 hover:bg-purple-100 hover:shadow-lg'>
            <h1 className='text-center text-xl md:text-2xl font-bold text-purple-600'>Learn Expressions!</h1>
            <p className='text-center text-lg mt-2'>Master idioms, phrasal verbs, and common sentences used in real life!</p>
            <div className='w-full flex gap-4 justify-center mt-auto pt-4'>
              <div data-testid='expressions-example' onClick={(e) => { e.stopPropagation(); navigate('/expressions', { state: { word: 'spill the beans' } }) }} className='flex text-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <p>🫘 Spill the beans</p>
              </div>
            </div>
            <div className='w-full flex gap-4 justify-center'>
              <div data-testid='expressions-example2' onClick={(e) => { e.stopPropagation(); navigate('/expressions', { state: { word: 'piece of cake' } }) }} className='flex text-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <p>🍰 Piece of cake</p>
              </div>
              <div data-testid='expressions-more' onClick={(e) => { e.stopPropagation(); navigate('/expressions') }} className='text-center flex justify-center items-center text-lg mt-2 border bg-white px-4 py-3 rounded cursor-pointer hover:bg-purple-200 hover:shadow-xl'>
                <PlusIcon />
                <p> More</p>
              </div>
            </div>
          </div>
        </div>

        {currentUser && savedWords.length > 0 && (<div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 mt-6">Saved Words</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedWords.map((word) => (
                    <WordCard
                        key={word.word}
                        word={word.word}
                        lastPractice={word.lastPractice || word.createdAt}
                        progress={word.progress}
                        showDelete={true}
                    />
                ))}
            </div>
            {loadMore && savedWords.length > 0 && (
                <button
                    onClick={() => getWordsPaginated(true)}
                    disabled={loadingWords}
                    className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                    {loadingWords ? "Loading..." : "Load More"}
                </button>
            )}
        </div>)}
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  color: string;
  message?: string;
  animateIcon?: boolean;
  onClick?: () => void;
}

function StatCard({ icon: Icon, title, value, color, message, animateIcon, onClick }: StatCardProps) {
  return (
    <div 
      className={`bg-white rounded-xl p-4 shadow-sm border border-gray-200 w-full md:w-auto min-w-[200px] hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4">
        <div className={`p-3 rounded-lg bg-gray-50`}>
          <Icon className={`w-8 h-8 ${color} ${animateIcon ? 'animate-bounce' : ''}`} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
      </div>
      {message && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-600 font-medium italic">"{message}"</p>
        </div>
      )}
    </div>
  );
}

function WordOfDay({ word, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 w-fit pr-8">
      <div className="flex items-center space-x-4">
        <div className={`${color} bg-opacity-10 p-3 rounded-lg`}>
          <LucideWholeWord className={`w-6 h-6 ${color}`} />
        </div>
        <div>
          <p className="text-sm text-gray-500">A palavra do dia é</p>
          <p className="text-xl font-semibold text-gray-900">{word}</p>
        </div>
      </div>
    </div>
  );
}